import React, { useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Quote, CheckCircle2 } from 'lucide-react';
import { getCaseStudy } from '../components/caseStudiesData';
import { CtaBand } from '../components/page/CtaBand';
import { Badge } from '../components/ui/Badge';
import { Reveal } from '../components/ui/Reveal';
export const CaseStudyDetail = () => {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const navigate = useNavigate();
  const study = slug ? getCaseStudy(slug) : undefined;
  useEffect(() => {
    if (!study) {
      navigate('/case-studies');
    }
  }, [study, navigate]);
  if (!study) return null;
  return <div className="pt-24 pb-12" data-id="element-1735">
      {/* Hero Section */}
      <section className="relative pt-12 pb-16 lg:pt-20 lg:pb-24 overflow-hidden border-b border-gray-100" data-id="element-1736">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-zano-lavender/30 rounded-full blur-3xl -z-10" data-id="element-1737" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1738">
          <Link to="/case-studies" className="inline-flex items-center gap-2 text-sm font-medium text-gray-500 hover:text-zano-pink transition-colors mb-8" data-id="element-1739">
            <ArrowLeft size={16} data-id="element-1740" /> Back to Case Studies
          </Link>

          <div className="flex flex-col lg:flex-row gap-12 lg:gap-20 items-start" data-id="element-1741">
            <div className="lg:w-3/5" data-id="element-1742">
              <div className="flex flex-wrap gap-2 mb-6" data-id="element-1743">
                {study.tags.map(tag => <Badge key={tag} className="bg-blue-50 text-blue-700 border border-blue-200" data-id="element-1744">
                    {tag}
                  </Badge>)}
              </div>
              <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-gray-900 mb-6 leading-tight" data-id="element-1745">
                {study.title}
              </h1>
              <p className="text-xl text-gray-600 leading-relaxed mb-8" data-id="element-1746">
                {study.subtitle}
              </p>
            </div>

            <div className="lg:w-2/5 w-full" data-id="element-1747">
              <div className="bg-gray-50 rounded-3xl p-8 border border-gray-100" data-id="element-1748">
                <div className={`w-full h-32 rounded-xl flex items-center justify-center mb-8 ${study.logoBg}`} data-id="element-1749">
                  <img src={study.logo} alt={`${study.brand} logo`} className="max-h-16 max-w-[200px] object-contain" data-id="element-1750" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-4" data-id="element-1751">
                  Key Results
                </h3>
                <ul className="space-y-3" data-id="element-1752">
                  {study.results.map((result, i) => <li key={i} className="flex items-start gap-3 text-gray-700" data-id="element-1753">
                      <CheckCircle2 size={20} className="text-zano-pink shrink-0 mt-0.5" data-id="element-1754" />
                      <span data-id="element-1755">{result}</span>
                    </li>)}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16 lg:py-24" data-id="element-1756">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1757">
          <div className="flex flex-col lg:flex-row gap-16" data-id="element-1758">
            {/* Main Content */}
            <div className="lg:w-2/3 prose prose-lg prose-pink max-w-none" data-id="element-1759">
              <Reveal data-id="element-1760">
                <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3" data-id="element-1761">
                  <span className="text-yellow-500" data-id="element-1762">⚡</span> The Challenge
                </h2>
                {study.challenge.map((p, i) => <p key={i} className="text-gray-700 leading-relaxed mb-4" data-id="element-1763">
                    {p}
                  </p>)}
              </Reveal>

              <Reveal delay={0.1} data-id="element-1764">
                <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6 flex items-center gap-3" data-id="element-1765">
                  <span className="text-blue-500" data-id="element-1766">🚀</span> The Solution: Using
                  Zano
                </h2>
                <p className="text-gray-700 leading-relaxed mb-4" data-id="element-1767">
                  {study.solution.intro}
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-8" data-id="element-1768">
                  {study.solution.bullets.map((bullet, i) => <li key={i} data-id="element-1769">{bullet}</li>)}
                </ul>
              </Reveal>

              <Reveal delay={0.2} data-id="element-1770">
                <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6 flex items-center gap-3" data-id="element-1771">
                  <span className="text-purple-500" data-id="element-1772">🔍</span> How It Worked
                </h2>
                <ol className="list-decimal pl-6 space-y-3 text-gray-700 mb-8" data-id="element-1773">
                  {study.howItWorked.map((step, i) => <li key={i} className="pl-2" data-id="element-1774">
                      {step}
                    </li>)}
                </ol>
              </Reveal>

              <Reveal delay={0.3} data-id="element-1775">
                <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6 flex items-center gap-3" data-id="element-1776">
                  <span className="text-emerald-500" data-id="element-1777">📈</span> The Results
                </h2>
                <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-8" data-id="element-1778">
                  {study.resultsDetailed.map((result, i) => <li key={i} data-id="element-1779">{result}</li>)}
                </ul>
              </Reveal>

              {study.keyInsight && <Reveal delay={0.4} data-id="element-1780">
                  <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6 flex items-center gap-3" data-id="element-1781">
                    <span className="text-amber-500" data-id="element-1782">💡</span> Key Insight
                  </h2>
                  <p className="text-gray-700 leading-relaxed bg-amber-50 p-6 rounded-2xl border border-amber-100" data-id="element-1783">
                    {study.keyInsight}
                  </p>
                </Reveal>}
            </div>

            {/* Sidebar */}
            <div className="lg:w-1/3 space-y-8" data-id="element-1784">
              <Reveal delay={0.2} data-id="element-1785">
                <div className="bg-zano-blush/30 rounded-3xl p-8 border border-zano-pink/20" data-id="element-1786">
                  <Quote size={32} className="text-zano-pink mb-6" data-id="element-1787" />
                  <p className="text-xl text-gray-900 font-medium italic leading-relaxed mb-6" data-id="element-1788">
                    "{study.quote.text}"
                  </p>
                  <div data-id="element-1789">
                    <p className="font-bold text-gray-900" data-id="element-1790">
                      {study.quote.author}
                    </p>
                    <p className="text-sm text-gray-600" data-id="element-1791">{study.quote.role}</p>
                  </div>
                </div>
              </Reveal>

              <Reveal delay={0.3} data-id="element-1792">
                <div className="bg-gray-50 rounded-3xl p-8 border border-gray-100" data-id="element-1793">
                  <h3 className="text-lg font-bold text-gray-900 mb-4" data-id="element-1794">
                    About {study.brand}
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed mb-6" data-id="element-1795">
                    {study.about.text}
                  </p>
                  <a href={study.about.website} target="_blank" rel="noopener noreferrer" className="text-sm font-semibold text-zano-pink hover:text-zano-pinkDark transition-colors" data-id="element-1796">
                    Visit Website &rarr;
                  </a>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <CtaBand title="Ready to see similar results?" subtitle="Join the top agencies and brands using Zano to scale their influencer campaigns." primaryCta={{
      label: 'Start Free Trial',
      to: '/pricing'
    }} data-id="element-1797" />
    </div>;
};